import { createContext, useContext, type ReactNode } from "react";
import type { SurfaceSnapshot, TaskActionItem } from "@/services/api/agentic-v2/types";
import type { VisibilityScope } from "@/components/agentic-v2/VisibilityFilter";
import type { MeetingSummary } from "@/services/api/meetings/types";
import type { HitlPendingItem } from "@/components/hitl/types";
import type { ApprovalJumpLink } from "@/components/home/approvalQueue";
import type { HomeMeeting, HomeStatItem, HomeTask } from "@/components/home/types";
import type { OpenHomeTaskOptions } from "@/components/review/reviewOpenMode";

export type HitlTaskActions = {
  onApprove: (item: HitlPendingItem) => void | Promise<void>;
  onReject: (item: HitlPendingItem) => void | Promise<void>;
  onRequestChanges: (item: HitlPendingItem) => void | Promise<void>;
  onApplyAction: (item: HitlPendingItem, action: string) => void | Promise<void>;
};

export interface HomeSurfaceContextValue {
  snapshot: SurfaceSnapshot | null;
  surfaceId: string;
  personaName: string;
  standupBrief: string;
  tasks: HomeTask[];
  /** Needs-me tasks assigned to the viewer (home review queue). */
  reviewQueueTasks: HomeTask[];
  /** Agent/pipeline onboarding tasks in flight. */
  inProgressQueueTasks: HomeTask[];
  meetings: HomeMeeting[];
  homeStats: HomeStatItem[];
  loading: boolean;
  /** Snapshot fetch failed while the shell is already visible. */
  snapshotError?: unknown;
  isRefreshing: boolean;
  homeContentSignature: string;
  refreshAll: () => void;
  needsMeTasks: TaskActionItem[];
  inProgressTasks: TaskActionItem[];
  meetingSummaries: MeetingSummary[];
  hitlItems: HitlPendingItem[];
  hitlActions: HitlTaskActions;
  pendingOpenTaskId: string | null;
  setPendingOpenTaskId: (id: string | null) => void;
  activeTask: HomeTask | null;
  setActiveTask: (task: HomeTask | null) => void;
  onTaskComplete: (taskId: string) => void;
  completedTaskIds: Set<string>;
  approvalQueueCount: number;
  approvalQueue: ApprovalJumpLink[];
  reviewModeOpen: boolean;
  setReviewModeOpen: (open: boolean) => void;
  reviewStartLinkId: string | null;
  openReviewMode: (linkId?: string | null) => void;
  closeReviewMode: () => void;
  openHomeTask: (task: HomeTask, options?: OpenHomeTaskOptions) => void;
  taskReviewOpen: boolean;
  taskReviewItem: HitlPendingItem | null;
  closeTaskReview: () => void;
  /** View-as subject for caseload-scoped widgets (artefacts, etc.). */
  asUserId?: string;
  /** Default caseload scope for home widgets that call list APIs directly. */
  caseloadVisibility?: VisibilityScope;
  /** Task list scope — widened to ``all`` on shared-queue surfaces (matches snapshot). */
  taskListVisibility?: VisibilityScope;
}

const HomeSurfaceContext = createContext<HomeSurfaceContextValue | null>(null);

export function HomeSurfaceProvider({
  value,
  children,
}: {
  value: HomeSurfaceContextValue;
  children: ReactNode;
}) {
  return (
    <HomeSurfaceContext.Provider value={value}>{children}</HomeSurfaceContext.Provider>
  );
}

export function useHomeSurfaceContext(): HomeSurfaceContextValue {
  const ctx = useContext(HomeSurfaceContext);
  if (!ctx) {
    throw new Error("useHomeSurfaceContext must be used within HomeSurfaceProvider");
  }
  return ctx;
}

export function useHomeSurfaceOptional(): HomeSurfaceContextValue | null {
  return useContext(HomeSurfaceContext);
}
